import { unwrap, type Schemas } from '@mrigl/api-client';
import { textDirection, type Locale } from '@mrigl/shared';
import { Alert, Card, SelectField, TextField } from '@mrigl/ui';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore } from '../catalog/shared.js';

type Report = Schemas['AnalyticsReport'];
type Granularity = Report['granularity'];
type Preset = '7d' | '30d' | '90d' | 'month' | 'custom';

/** Tunis calendar date, `days` from today (negative = past). */
function tunisDate(days = 0): string {
  const date = new Date(Date.now() + days * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Tunis' }).format(date);
}

function monthStart(day = tunisDate()): string {
  return `${day.slice(0, 8)}01`;
}

/** Change against the previous period, e.g. "+12 %"; null when there is nothing to compare. */
export function change(current: string, previous: string): number | null {
  const before = Number(previous);
  if (before === 0) return null;
  return Math.round(((Number(current) - before) / before) * 100);
}

function Kpi({
  label,
  value,
  delta,
  hint,
  t,
}: {
  readonly label: string;
  readonly value: string;
  readonly delta: number | null;
  readonly hint?: string;
  readonly t: I18n['t'];
}) {
  return (
    <div className="admin-kpi">
      <span className="mrigl-muted">{label}</span>
      <strong className="admin-kpi__value">{value}</strong>
      {hint === undefined ? null : <span className="mrigl-muted">{hint}</span>}
      <span className="mrigl-muted">
        {delta === null
          ? t('admin.analytics.noComparison')
          : t('admin.analytics.change', {
              percent: delta > 0 ? `+${String(delta)}` : String(delta),
            })}
      </span>
    </div>
  );
}

function plotX(index: number, count: number, step: number, rtl: boolean): number {
  return (rtl ? count - 1 - index : index) * step;
}

/** Bar/area chart of revenue per period, with the same data as a table for screen readers. */
function SalesChart({ report, locale }: { readonly report: Report; readonly locale: Locale }) {
  const { t, formatPrice } = useI18n();
  const rtl = textDirection(locale) === 'rtl';
  const values = report.series.map((point) => Number(point.revenue.minor));
  const max = Math.max(1, ...values);
  const step = 18;
  const width = Math.max(report.series.length * step, 300);
  const points = report.series.map((point, index) => {
    const x = plotX(index, report.series.length, step, rtl) + 9;
    const y = 156 - (Number(point.revenue.minor) / max) * 140;
    return `${String(x)},${String(y)}`;
  });
  const area =
    report.series.length === 0
      ? ''
      : `${rtl ? String(width) : '0'},156 ${points.join(' ')} ${rtl ? '0' : String(width)},156`;
  return (
    <>
      <svg
        className="admin-chart"
        viewBox={`0 0 ${String(width)} 160`}
        role="img"
        aria-label={t('admin.analytics.chartLabel', {
          from: report.from,
          to: report.to,
          total: formatPrice(report.totals.revenue.minor),
        })}
        preserveAspectRatio="none"
      >
        <polygon className="admin-chart__area" points={area} />
        <polyline className="admin-chart__line" points={points.join(' ')} fill="none" />
        {report.series.map((point, index) => {
          const height = (Number(point.revenue.minor) / max) * 150;
          return (
            <rect
              key={point.period}
              x={plotX(index, report.series.length, step, rtl) + 2}
              y={156 - height}
              width={14}
              height={height}
              rx={2}
            >
              <title>{`${point.period}: ${formatPrice(point.revenue.minor)}`}</title>
            </rect>
          );
        })}
      </svg>
      <details>
        <summary>{t('admin.analytics.showTable')}</summary>
        <div className="admin-table-wrap">
          <table className="mrigl-table">
            <thead>
              <tr>
                <th scope="col">{t('admin.analytics.period')}</th>
                <th scope="col">{t('admin.analytics.revenue')}</th>
                <th scope="col">{t('admin.analytics.orders')}</th>
              </tr>
            </thead>
            <tbody>
              {report.series.map((point) => (
                <tr key={point.period}>
                  <td>{point.period}</td>
                  <td>{formatPrice(point.revenue.minor)}</td>
                  <td>{point.orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}

function OutcomesChart({ report, locale }: { readonly report: Report; readonly locale: Locale }) {
  const { t } = useI18n();
  const rtl = textDirection(locale) === 'rtl';
  const max = Math.max(
    1,
    ...report.series.flatMap((point) => [point.orders, point.cancelled, point.returned]),
  );
  const group = 28;
  const width = Math.max(report.series.length * group, 300);
  const bar = (value: number) => (value / max) * 150;
  return (
    <svg
      className="admin-chart"
      viewBox={`0 0 ${String(width)} 160`}
      role="img"
      aria-label={t('admin.analytics.outcomesLabel', { from: report.from, to: report.to })}
      preserveAspectRatio="none"
    >
      {report.series.map((point, index) => {
        const x = plotX(index, report.series.length, group, rtl);
        return (
          <g key={point.period}>
            <rect
              className="admin-chart__sold"
              x={x + 2}
              y={156 - bar(point.orders)}
              width={7}
              height={bar(point.orders)}
              rx={1}
            >
              <title>{`${point.period}: ${String(point.orders)}`}</title>
            </rect>
            <rect
              className="admin-chart__cancelled"
              x={x + 10}
              y={156 - bar(point.cancelled)}
              width={7}
              height={bar(point.cancelled)}
              rx={1}
            >
              <title>{`${point.period}: ${String(point.cancelled)}`}</title>
            </rect>
            <rect
              className="admin-chart__returned"
              x={x + 18}
              y={156 - bar(point.returned)}
              width={7}
              height={bar(point.returned)}
              rx={1}
            >
              <title>{`${point.period}: ${String(point.returned)}`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}

function ShareBars({
  rows,
  max,
  format,
}: {
  readonly rows: { readonly key: string; readonly label: string; readonly value: number }[];
  readonly max: number;
  readonly format: (value: number) => string;
}) {
  return (
    <ul className="admin-hbars">
      {rows.map((row) => (
        <li key={row.key}>
          <div className="admin-hbars__meta">
            <span dir="auto">{row.label}</span>
            <span>{format(row.value)}</span>
          </div>
          <div className="admin-hbars__track" aria-hidden="true">
            <span
              style={{ inlineSize: `${String(Math.round((row.value / Math.max(max, 1)) * 100))}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function PeriodToolbar({
  from,
  to,
  granularity,
  preset,
  onFrom,
  onTo,
  onGranularity,
  onPreset,
}: {
  readonly from: string;
  readonly to: string;
  readonly granularity: Granularity;
  readonly preset: Preset;
  readonly onFrom: (value: string) => void;
  readonly onTo: (value: string) => void;
  readonly onGranularity: (value: Granularity) => void;
  readonly onPreset: (value: Preset) => void;
}) {
  const { t } = useI18n();
  const presets = ['7d', '30d', '90d', 'month'] as const;
  return (
    <div className="admin-filters">
      <div className="admin-presets" role="group" aria-label={t('admin.analytics.range')}>
        {presets.map((value) => (
          <button
            key={value}
            type="button"
            className="mrigl-button mrigl-button--secondary"
            aria-pressed={preset === value}
            onClick={() => {
              onPreset(value);
            }}
          >
            {t(`admin.analytics.preset.${value}`)}
          </button>
        ))}
      </div>
      <TextField
        type="date"
        label={t('admin.analytics.from')}
        value={from}
        max={to}
        onChange={(event) => {
          onFrom(event.target.value);
        }}
      />
      <TextField
        type="date"
        label={t('admin.analytics.to')}
        value={to}
        min={from}
        onChange={(event) => {
          onTo(event.target.value);
        }}
      />
      <SelectField
        label={t('admin.analytics.granularity')}
        value={granularity}
        onChange={(event) => {
          onGranularity(event.target.value as Granularity);
        }}
        options={(['day', 'week', 'month'] as const).map((value) => ({
          value,
          label: t(`admin.analytics.granularity.${value}`),
        }))}
      />
    </div>
  );
}

function DashboardReport({
  storeId,
  report,
  compact,
}: {
  readonly storeId: string;
  readonly report: Report;
  readonly compact?: boolean;
}) {
  const { t, locale, formatPrice } = useI18n();
  const funnelTotal = Object.values(report.funnel).reduce((sum, value) => sum + value, 0);
  const percent = (value: number, total: number) =>
    total === 0 ? '0 %' : `${String(Math.round((value / total) * 100))} %`;
  return (
    <>
      <div className="admin-kpis">
        <Kpi
          t={t}
          label={t('admin.analytics.revenue')}
          value={formatPrice(report.totals.revenue.minor)}
          delta={change(report.totals.revenue.minor, report.previous.revenue.minor)}
        />
        <Kpi
          t={t}
          label={t('admin.analytics.orders')}
          value={String(report.totals.orders)}
          delta={change(String(report.totals.orders), String(report.previous.orders))}
        />
        <Kpi
          t={t}
          label={t('admin.analytics.averageBasket')}
          value={formatPrice(report.totals.averageBasket.minor)}
          delta={change(report.totals.averageBasket.minor, report.previous.averageBasket.minor)}
        />
        {compact === true ? null : (
          <>
            <Kpi
              t={t}
              label={t('admin.analytics.itemsSold')}
              value={String(report.totals.itemsSold)}
              delta={change(String(report.totals.itemsSold), String(report.previous.itemsSold))}
            />
            <Kpi
              t={t}
              label={t('admin.analytics.cancelled')}
              value={String(report.totals.cancelled)}
              hint={formatPrice(report.totals.cancelledAmount.minor)}
              delta={change(String(report.totals.cancelled), String(report.previous.cancelled))}
            />
            <Kpi
              t={t}
              label={t('admin.analytics.returned')}
              value={String(report.totals.returned)}
              hint={formatPrice(report.totals.returnedAmount.minor)}
              delta={change(String(report.totals.returned), String(report.previous.returned))}
            />
          </>
        )}
      </div>

      <div className={compact === true ? undefined : 'admin-dash'}>
        <Card labelledBy="sales-title">
          <h2 id="sales-title" className="mrigl-subheading">
            {t('admin.analytics.sales')}
          </h2>
          <SalesChart report={report} locale={locale} />
        </Card>
        {compact === true ? null : (
          <Card labelledBy="outcomes-title">
            <h2 id="outcomes-title" className="mrigl-subheading">
              {t('admin.analytics.outcomes')}
            </h2>
            <p className="mrigl-muted admin-chart-legend">
              <span className="admin-chart-legend__sold">{t('admin.analytics.orders')}</span>
              <span className="admin-chart-legend__cancelled">
                {t('admin.analytics.cancelled')}
              </span>
              <span className="admin-chart-legend__returned">{t('admin.analytics.returned')}</span>
            </p>
            <OutcomesChart report={report} locale={locale} />
          </Card>
        )}
      </div>

      {compact === true ? null : (
        <Card labelledBy="catalog-title">
          <h2 id="catalog-title" className="mrigl-subheading">
            {t('admin.analytics.catalog')}
          </h2>
          <div className="admin-kpis admin-kpis--compact">
            <div className="admin-kpi">
              <span className="mrigl-muted">{t('admin.catalog.products')}</span>
              <strong className="admin-kpi__value">{report.catalog.products}</strong>
            </div>
            <div className="admin-kpi">
              <span className="mrigl-muted">{t('admin.product.status.active')}</span>
              <strong className="admin-kpi__value">{report.catalog.live}</strong>
            </div>
            <div className="admin-kpi">
              <span className="mrigl-muted">{t('admin.product.status.draft')}</span>
              <strong className="admin-kpi__value">{report.catalog.draft}</strong>
            </div>
            <div className="admin-kpi">
              <span className="mrigl-muted">{t('admin.product.status.archived')}</span>
              <strong className="admin-kpi__value">{report.catalog.archived}</strong>
            </div>
            <div className="admin-kpi">
              <span className="mrigl-muted">{t('admin.catalog.collections')}</span>
              <strong className="admin-kpi__value">{report.catalog.collections}</strong>
            </div>
          </div>
        </Card>
      )}

      <div className={compact === true ? undefined : 'admin-dash'}>
        <Card labelledBy="top-title">
          <h2 id="top-title" className="mrigl-subheading">
            {t('admin.analytics.topProducts')}
          </h2>
          {report.topProducts.length === 0 ? (
            <p className="mrigl-muted">{t('admin.analytics.noSales')}</p>
          ) : (
            <>
              {compact === true ? null : (
                <ShareBars
                  max={Number(report.topProducts[0]?.revenue.minor ?? 1)}
                  format={(value) => formatPrice(String(value))}
                  rows={report.topProducts.map((item) => ({
                    key: item.productId,
                    label: item.title,
                    value: Number(item.revenue.minor),
                  }))}
                />
              )}
              <div className="admin-table-wrap">
                <table className="mrigl-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('admin.order.column.item')}</th>
                      <th scope="col">{t('admin.order.column.quantity')}</th>
                      <th scope="col">{t('admin.analytics.revenue')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.topProducts.map((item) => (
                      <tr key={item.productId}>
                        <td dir="auto">
                          <Link to={`/stores/${storeId}/products/${item.productId}`}>
                            {item.title}
                          </Link>
                        </td>
                        <td>{item.quantity}</td>
                        <td>{formatPrice(item.revenue.minor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>

        {compact === true ? null : (
          <Card labelledBy="regions-title">
            <h2 id="regions-title" className="mrigl-subheading">
              {t('admin.analytics.byGovernorate')}
            </h2>
            {report.byGovernorate.length === 0 ? (
              <p className="mrigl-muted">{t('admin.analytics.noSales')}</p>
            ) : (
              <>
                <ShareBars
                  max={Number(report.byGovernorate[0]?.revenue.minor ?? 1)}
                  format={(value) => formatPrice(String(value))}
                  rows={report.byGovernorate.map((row) => ({
                    key: row.governorate ?? 'pickup',
                    label: row.name ?? t('admin.order.delivery.pickup'),
                    value: Number(row.revenue.minor),
                  }))}
                />
                <div className="admin-table-wrap">
                  <table className="mrigl-table">
                    <thead>
                      <tr>
                        <th scope="col">{t('admin.analytics.region')}</th>
                        <th scope="col">{t('admin.analytics.orders')}</th>
                        <th scope="col">{t('admin.analytics.revenue')}</th>
                        <th scope="col">{t('admin.analytics.share')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byGovernorate.map((row) => (
                        <tr key={row.governorate ?? 'pickup'}>
                          <td>{row.name ?? t('admin.order.delivery.pickup')}</td>
                          <td>{row.orders}</td>
                          <td>{formatPrice(row.revenue.minor)}</td>
                          <td>{percent(row.orders, report.totals.orders)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
        )}
      </div>

      {compact === true ? null : (
        <Card labelledBy="funnel-title">
          <h2 id="funnel-title" className="mrigl-subheading">
            {t('admin.analytics.funnel')}
          </h2>
          <ul className="admin-funnel">
            {(
              ['placed', 'confirmed', 'shipped', 'delivered', 'cancelled', 'returned'] as const
            ).map((status) => (
              <li key={status}>
                <div className="admin-funnel__meta">
                  <span>{t(`admin.order.status.${status}`)}</span>
                  <span>
                    {report.funnel[status]} ({percent(report.funnel[status], funnelTotal)})
                  </span>
                </div>
                <div className="admin-funnel__track" aria-hidden="true">
                  <span
                    className={`admin-funnel__fill admin-funnel__fill--${status}`}
                    style={{
                      inlineSize: `${String(Math.round((report.funnel[status] / Math.max(funnelTotal, 1)) * 100))}%`,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
          <h3 className="mrigl-subheading">{t('admin.analytics.payments')}</h3>
          <dl className="admin-details">
            <dt>{t('admin.order.paymentStatus.paid')}</dt>
            <dd>{formatPrice(report.payments.paid.minor)}</dd>
            <dt>{t('admin.order.paymentStatus.pending')}</dt>
            <dd>{formatPrice(report.payments.pending.minor)}</dd>
            <dt>{t('admin.order.paymentStatus.refunded')}</dt>
            <dd>{formatPrice(report.payments.refunded.minor)}</dd>
          </dl>
        </Card>
      )}
    </>
  );
}

function applyPreset(value: Preset): { from: string; to: string } | null {
  const today = tunisDate();
  if (value === '7d') return { from: tunisDate(-6), to: today };
  if (value === '30d') return { from: tunisDate(-29), to: today };
  if (value === '90d') return { from: tunisDate(-89), to: today };
  if (value === 'month') return { from: monthStart(today), to: today };
  return null;
}

function useAnalytics(storeId: string, from: string, to: string, granularity: Granularity) {
  const { t } = useI18n();
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (from === '' || to === '') return;
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/analytics', {
        params: { path: { storeId }, query: { from, to, granularity } },
      }),
    )
      .then((result) => {
        setReport(result);
        setError(null);
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [storeId, from, to, granularity, t]);
  return { report, error };
}

/** Compact last-30-days snapshot on the store home. */
export function StoreOverview({ storeId }: { readonly storeId: string }) {
  const { t } = useI18n();
  const from = tunisDate(-29);
  const to = tunisDate();
  const { report, error } = useAnalytics(storeId, from, to, 'day');
  if (error !== null) return null;
  if (report === null) return <Loading />;
  return (
    <section className="mrigl-stack" aria-labelledby="overview-title">
      <div className="admin-pagehead">
        <h2 id="overview-title" className="mrigl-subheading">
          {t('admin.analytics.overview')}
        </h2>
        <Link className="mrigl-button mrigl-button--secondary" to={`/stores/${storeId}/analytics`}>
          {t('admin.analytics.seeDashboard')}
        </Link>
      </div>
      <DashboardReport storeId={storeId} report={report} compact />
    </section>
  );
}

/** Sales dashboard of the store (orders not cancelled or returned, Tunis dates). */
export function AnalyticsPage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [from, setFrom] = useState(tunisDate(-29));
  const [to, setTo] = useState(tunisDate());
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [preset, setPreset] = useState<Preset>('30d');
  const { report, error } = useAnalytics(storeId, from, to, granularity);

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null) return <Loading />;

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.analytics.title')}</h1>
      <p className="mrigl-muted">{t('admin.analytics.definition')}</p>
      <PeriodToolbar
        from={from}
        to={to}
        granularity={granularity}
        preset={preset}
        onFrom={(value) => {
          setPreset('custom');
          setFrom(value);
        }}
        onTo={(value) => {
          setPreset('custom');
          setTo(value);
        }}
        onGranularity={setGranularity}
        onPreset={(value) => {
          const next = applyPreset(value);
          if (next === null) return;
          setPreset(value);
          setFrom(next.from);
          setTo(next.to);
        }}
      />
      <p>
        <a
          href={`/admin-api/v1/stores/${storeId}/orders-export.csv?from=${from}&to=${to}`}
          download
        >
          {t('admin.analytics.exportOrders')}
        </a>
      </p>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {report === null ? (
        error === null ? (
          <Loading />
        ) : null
      ) : (
        <DashboardReport storeId={storeId} report={report} />
      )}
    </div>
  );
}
